import { array } from "zod";
import type { ZodType } from "zod";

import { flightFormLocationSchema } from "@/app/ui/flights/validation";
import { useValidatedLocalStorage } from "@/hooks/useValidatedLocalStorage";
import type { City, FlightFormLocation } from "@/types/types";

import { citySchema } from "../utils/locations";

export const PREVIOUS_CITY_SEARCH_STORAGE_KEY = "tz_previousCitySearches@2";
export const PREVIOUS_AIRPORT_SEARCH_STORAGE_KEY =
  "tz_previousAirportSearches@2";
const MAX_SEARCH_COUNT = 5;

const cityArraySchema = array(citySchema);
const flightFormLocationArraySchema = array(flightFormLocationSchema);

const usePreviousSearches = <T extends { id: string }>(
  storageKey: string,
  schema: ZodType<T[]>,
) => {
  const [previousSearches, setPreviousSearches] = useValidatedLocalStorage<T[]>(
    storageKey,
    [],
    schema,
  );

  return {
    previousSearches: previousSearches || [],
    addPreviousSearch: (newItem: T) => {
      if (!newItem) return;

      setPreviousSearches((items) =>
        [
          newItem,
          ...(items || []).filter((item) => item.id !== newItem.id),
        ].slice(0, MAX_SEARCH_COUNT),
      );
    },
    removePreviousSearch: (itemId: string) => {
      setPreviousSearches((items) =>
        (items || []).filter((item) => item.id !== itemId),
      );
    },
  };
};

export const usePreviousCitySearches = () => {
  return usePreviousSearches<City>(
    PREVIOUS_CITY_SEARCH_STORAGE_KEY,
    cityArraySchema,
  );
};

export const usePreviousAirportSearches = () => {
  return usePreviousSearches<FlightFormLocation>(
    PREVIOUS_AIRPORT_SEARCH_STORAGE_KEY,
    flightFormLocationArraySchema,
  );
};
